import type { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  size?: "sm" | "md" | "lg";
}

export function EmptyState({ icon: Icon, title, description, action, size = "md" }: EmptyStateProps) {
  const sizes = {
    sm: {
      container: "py-8",
      iconSize: "w-12 h-12",
      iconBg: "w-20 h-20",
      title: "text-base",
      description: "text-xs",
    },
    md: {
      container: "py-12",
      iconSize: "w-16 h-16",
      iconBg: "w-28 h-28",
      title: "text-lg",
      description: "text-sm",
    },
    lg: {
      container: "py-16",
      iconSize: "w-20 h-20",
      iconBg: "w-32 h-32",
      title: "text-xl",
      description: "text-base",
    },
  };

  const s = sizes[size];

  return (
    <div className={`${s.container} text-center`}>
      {/* Icon with animated background */}
      <div className="relative inline-flex items-center justify-center mb-4">
        <div className={`${s.iconBg} bg-[var(--muted)] rounded-full opacity-30 absolute animate-pulse`}></div>
        <Icon className={`${s.iconSize} text-[var(--muted-foreground)] opacity-50 relative z-10`} strokeWidth={1.5} />
      </div>

      {/* Title */}
      <h3 className={`${s.title} font-semibold text-[var(--foreground)] mb-2`}>
        {title}
      </h3>

      {/* Description */}
      <p className={`${s.description} text-[var(--muted-foreground)] max-w-md mx-auto mb-6`}>
        {description}
      </p>

      {/* Optional Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-steel-bg)] border border-[var(--color-steel)] text-[var(--color-steel)] rounded-lg hover:bg-[var(--color-steel-bg)] hover:shadow-[0_0_12px_rgba(122,168,200,0.15)] transition-all font-medium text-sm"
        >
          {action.icon && <action.icon className="w-4 h-4" />}
          {action.label}
        </button>
      )}
    </div>
  );
}
